/**
 * ServingCitiesCard — vendor / pro / venue dashboard widget to manage additional
 * service cities. Base city (set during KYC) is shown as a locked chip; vendor
 * can ADD additional cities from the admin-curated master list, with a clear
 * "no transport allowance" warning displayed inline + on first add.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Warning, X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServingCitiesCard = () => {
  const [base, setBase] = useState('');
  const [serving, setServing] = useState([]);
  const [master, setMaster] = useState([]);
  const [picker, setPicker] = useState('');
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(null);  // { city } when showing inline warning modal

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/serving-cities`, { withCredentials: true });
      setBase(data.base_city || '');
      setServing(data.serving_cities || []);
      setMaster(data.master_cities || []);
    } catch (err) {
      console.error('Failed to fetch serving cities', err);
    }
  };

  const _putServing = async (cities, nextBase) => {
    setSaving(true);
    try {
      const payload = { cities };
      if (nextBase) payload.base_city = nextBase;
      const { data } = await axios.put(
        `${BACKEND_URL}/api/me/serving-cities`,
        payload,
        { withCredentials: true },
      );
      setServing(data.serving_cities || []);
      if (data.base_city) setBase(data.base_city);
      toast.success('Saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const save = (cities) => _putServing(cities, null);
  const saveBase = (city) => _putServing(serving, city);

  const requestAdd = () => {
    const city = (picker || '').trim();
    if (!city) return;
    if (city === base) {
      toast.info('That is already your base city');
      return;
    }
    if (serving.includes(city)) {
      toast.info('Already in your serving list');
      return;
    }
    // Show confirmation modal with no-transport-allowance warning.
    setWarn({ city });
  };

  const confirmAdd = async () => {
    if (!warn) return;
    const next = [...serving, warn.city];
    setWarn(null);
    setPicker('');
    await save(next);
  };

  const remove = async (city) => {
    const next = serving.filter((c) => c !== city);
    await save(next);
  };

  // Filter dropdown options: exclude base + already-added.
  const available = master.filter((c) => c !== base && !serving.includes(c));

  return (
    <>
      <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="serving-cities-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <MapPin size={20} className="text-brand-primary" /> Serving Cities
            <span className="text-xs font-normal text-red-600">(REQUIRED)</span>
          </CardTitle>
          <CardDescription>
            Pick your <span className="font-semibold">base city</span> (where you operate from — full transport allowance covered)
            then add any extra cities you're willing to travel to. For those extras BeeWeddy
            <span className="font-semibold text-red-600"> will not provide any transport allowance</span>.
            This is independent of KYC, but is mandatory for customers to find you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Base city — editable dropdown until set, then a locked chip with a tiny "Change" link. */}
          <div className="mb-4">
            <p className="text-xs font-bold tracking-wider uppercase text-text-secondary mb-2">Base City <span className="text-red-500">*</span></p>
            {base ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 bg-brand-primary text-sand-50 px-3 py-1.5 rounded-full text-sm font-bold" data-testid="base-city-chip">
                  <MapPin size={14} weight="fill" /> {base}
                </span>
                <button type="button" onClick={() => setBase('')}
                  className="text-[11px] uppercase font-bold tracking-wider text-brand-primary hover:underline"
                  data-testid="base-city-change">
                  Change
                </button>
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) saveBase(e.target.value); }}
                  className="border border-[#E8E1D5] rounded h-9 px-2 text-sm bg-white flex-1 max-w-xs"
                  data-testid="base-city-picker"
                  disabled={saving}>
                  <option value="">— Pick your base city —</option>
                  {master.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-[10px] text-amber-700 italic">Required so customers can find you</p>
              </div>
            )}
          </div>

          {/* Additional cities */}
          <div className="mb-4">
            <p className="text-xs font-bold tracking-wider uppercase text-text-secondary mb-2">
              Additional Serving Cities ({serving.length})
            </p>
            {serving.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No extra cities yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {serving.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 bg-amber-100 border border-amber-300 text-amber-900 px-3 py-1.5 rounded-full text-sm font-medium"
                    data-testid={`serving-chip-${c}`}>
                    <MapPin size={14} /> {c}
                    <button type="button" onClick={() => remove(c)} disabled={saving}
                      className="ml-1 hover:bg-amber-300 rounded-full p-0.5"
                      data-testid={`serving-remove-${c}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Add picker */}
          {base && available.length > 0 && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs font-bold text-text-secondary block mb-1">Add a city</label>
                <select value={picker} onChange={(e) => setPicker(e.target.value)}
                  className="w-full border border-[#E8E1D5] rounded h-9 px-2 text-sm bg-white"
                  data-testid="serving-picker">
                  <option value="">— Select city —</option>
                  {available.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Button onClick={requestAdd} disabled={!picker || saving}
                className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
                data-testid="serving-add-btn">
                <Plus size={16} className="mr-1" /> Add
              </Button>
            </div>
          )}

          {available.length === 0 && base && (
            <p className="text-xs text-text-secondary italic">
              You're already serving every city on the platform — nice!
            </p>
          )}

          <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
            <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              <span className="font-bold">Heads up:</span> Any city you add here means customers from that city can
              find &amp; book you, but BeeWeddy will <span className="font-bold">not pay any transport / travel allowance</span>.
              You handle logistics on your own.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation modal */}
      {warn && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="serving-warn-modal">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 border-2 border-amber-500">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={22} className="text-amber-600" />
              <h3 className="font-heading text-xl">Add {warn.city}?</h3>
            </div>
            <p className="text-sm text-text-secondary mb-3">
              You're about to add <span className="font-bold text-text-primary">{warn.city}</span> to your serving cities.
            </p>
            <div className="bg-red-50 border border-red-200 px-3 py-3 rounded mb-4">
              <p className="text-sm font-bold text-red-700 mb-1">
                Transport allowance NOT provided
              </p>
              <p className="text-sm text-red-700">
                BeeWeddy will <span className="font-bold">not</span> reimburse any travel cost for events in this city.
                You're responsible for getting yourself + team to the venue.
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setWarn(null)}
                className="flex-1 py-2 border border-[#E8E1D5] rounded text-sm font-bold hover:bg-gray-50"
                data-testid="serving-warn-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmAdd}
                className="flex-1 py-2 bg-amber-600 text-white rounded text-sm font-bold hover:bg-amber-700"
                data-testid="serving-warn-confirm">
                I Understand, Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServingCitiesCard;
